import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiFetch } from "@/utils/api";

export function SystemsPage() {
  const { systemId } = useParams();
  const [systems, setSystems] = useState<Record<string, unknown>[]>([]);
  const [detail, setDetail] = useState<{
    system: Record<string, unknown>;
    bomLines: Record<string, unknown>[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        if (systemId) {
          const data = await apiFetch<{
            system: Record<string, unknown>;
            bomLines: Record<string, unknown>[];
          }>(`/systems/${systemId}`);
          setDetail(data);
        } else {
          const data = await apiFetch<{ systems: Record<string, unknown>[] }>("/systems");
          setSystems(data.systems);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [systemId]);

  if (loading) return <p className="text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-destructive">{error}</p>;

  if (systemId && detail) {
    return (
      <div className="space-y-4">
        <Link to="/systems" className="text-sm text-[#1e3a4f] hover:underline">
          ← All systems
        </Link>
        <h2 className="text-2xl font-bold text-[#1e3a4f]">{String(detail.system.name)}</h2>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2">Part</th>
                <th className="text-right px-4 py-2">Req</th>
                <th className="text-right px-4 py-2">On-hand</th>
              </tr>
            </thead>
            <tbody>
              {detail.bomLines.map((line) => (
                <tr key={String(line.id)} className="border-t">
                  <td className="px-4 py-2">
                    <Link
                      to={`/systems/${systemId}/parts/${line.part_id}/history`}
                      className="font-medium hover:underline"
                    >
                      {String(line.part_name)}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{String(line.quantity_required)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{String(line.on_hand)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-[#1e3a4f]">Systems</h2>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {systems.map((s) => (
          <li key={String(s.id)}>
            <Link
              to={`/systems/${s.id}`}
              className="block bg-white rounded-lg shadow px-4 py-3 hover:ring-2 ring-[#1e3a4f]/30"
            >
              <span className="font-medium text-[#1e3a4f]">{String(s.name)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
